/**
 * Top-level paths the SPA owns.
 *
 * Anything NOT in this list that is a single path segment is a blog post slug
 * (the router maps `/:slug` to the blog page), which is what lets the canonical
 * tag and the not-found handling tell "/about" apart from "/2024-nissan-rogue".
 *
 * This list is mirrored by the rewrite globs in firebase.json. They must agree:
 * firebase.json has no `**` catch-all, so a route present here but missing
 * there returns a hard 404 in production. `scripts/prerender-blogs.mjs` fails
 * the build if the two drift apart.
 *
 * .mjs so both Vite and the plain-node build scripts can read it.
 */
export const TOP_LEVEL_ROUTES = [
  'login',
  'register',
  'cars',
  'search',
  'home',
  'about',
  'contact',
  'dealer',
  'faqs',
  'privacy',
  'terms',
  'blog',
  'reviews',
  'news',
  'comparison',
  'suvs-and-trucks',
  'sportscar',
  'stories',
  'chat',
  'settings',
  'notifications',
  'favorites',
  'saved-alerts',
  'payment',
  'post',
  'dashboard',
  'profile',
  'category',
];

export const META_DESCRIPTION_LIMIT = 160;

/**
 * Quill article HTML -> a plain-text meta description.
 *
 * Shared by the build-time prerender and the runtime Helmet tags so the
 * description Google reads in the raw HTML is the same one it reads after
 * rendering the page.
 */
export function metaDescriptionFromHtml(html, fallback = '') {
  const text = String(html || '')
    .replace(/<style[\s\S]*?<\/style>/gi, ' ')
    .replace(/<script[\s\S]*?<\/script>/gi, ' ')
    .replace(/<[^>]+>/g, ' ')
    .replace(/&nbsp;/g, ' ')
    .replace(/&amp;/g, '&')
    .replace(/&quot;/g, '"')
    .replace(/&#39;|&apos;/g, "'")
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/\s+/g, ' ')
    .trim();

  const source = text || String(fallback || '').trim();
  if (source.length <= META_DESCRIPTION_LIMIT) return source;

  const cut = source.slice(0, META_DESCRIPTION_LIMIT);
  const lastSpace = cut.lastIndexOf(' ');
  return `${(lastSpace > 80 ? cut.slice(0, lastSpace) : cut).trim()}…`;
}

/** The one origin every indexable English article consolidates to. */
export const PRIMARY_ORIGIN = 'https://www.carsnus.com';

/** Regional storefronts that keep their own canonical for non-article pages. */
export const REGIONAL_ORIGINS = [{ pattern: /(^|\.)carsnus\.pe$/i, origin: 'https://www.carsnus.pe' }];

/** "/About/" -> "/about"; "/" stays "/". */
export function normalizePath(pathname) {
  const path = String(pathname || '/')
    .split('?')[0]
    .split('#')[0]
    .replace(/\/{2,}/g, '/')
    .replace(/\/+$/, '');
  return path || '/';
}

export function isBlogPostPath(pathname) {
  const segments = normalizePath(pathname).split('/').filter(Boolean);
  return segments.length === 1 && !TOP_LEVEL_ROUTES.includes(segments[0].toLowerCase());
}

/**
 * The single URL that should represent this page in search.
 *
 * carsnus.com, www.carsnus.com, carsnus.pe and www.carsnus.pe all serve the
 * same build, so without this every page exists at four addresses and Google
 * picks its own representative. Marketplace pages stay on their regional
 * origin; blog articles are one English document, so they always point at the
 * primary origin no matter which domain served them.
 */
export function canonicalUrl(pathname, hostname = '') {
  const path = normalizePath(pathname);
  if (isBlogPostPath(path)) return PRIMARY_ORIGIN + path;

  const regional = REGIONAL_ORIGINS.find(({ pattern }) => pattern.test(hostname));
  const origin = regional ? regional.origin : PRIMARY_ORIGIN;
  return path === '/' ? `${origin}/` : origin + path;
}
